import { Typography, Box, Grid, Card, CardActionArea, CardContent, useTheme } from '@mui/material'
import { Icons } from '../../utils/utilities'
import { useUserStore } from '../../store/user.store'
import { useNavigate } from 'react-router-dom'

const COLORS = ['#E31A4B', '#F26922', '#F7B217', '#B6D433', '#209D48', '#3FBED5', '#745BA7', '#E31A4B', '#F26922']

const permissionMeta = {
    analytics: {
        label: 'Admin Dashboard',
        description: 'View comprehensive system analytics and high-level performance data.',
        icon: Icons.default.HomeIcon,
        path: '/admin/home'
    },
    academics: {
        label: 'Academics',
        description: 'Manage academic rules, curriculum structures, and educational standards.',
        icon: Icons.default.School,
        path: '/admin/academic-rules'
    },
    admission_exams: {
        label: 'Admission Exams',
        description: 'Oversee pre-post assessments and entrance examination processes.',
        icon: Icons.default.DomailAdd,
        path: '/admin/assessment'
    },
    announcements: {
        label: 'Announcements',
        description: 'Create and broadcast important updates to all platform users.',
        icon: Icons.default.Announcement,
        path: '/admin/announcement'
    },
    api_settings: {
        label: 'API Settings',
        description: 'Configure external integrations and manage system API credentials.',
        icon: Icons.default.Settings,
        path: '/admin/api-setting'
    },
    attendance_audit: {
        label: 'Attendance Audit',
        description: 'Review and verify attendance logs for online class sessions.',
        icon: Icons.default.EmojiPeopleIcon,
        path: '/admin/attendance'
    },
    auditing: {
        label: 'System Auditing',
        description: 'Comprehensive review of resource distribution and system activities.',
        icon: Icons.default.VisibilityIcon,
        path: '/admin/audit'
    },
    control_panel: {
        label: 'Control Panel',
        description: 'Access core administrative tools and global system configurations.',
        icon: Icons.default.Settings,
        path: '/admin/control-panel'
    },
    debug_user: {
        label: 'Debug User',
        description: 'Investigate user-specific issues and troubleshoot account behaviors.',
        icon: Icons.default.ManageAccounts,
        path: '/admin/debug-user'
    },
    debug_logs: {
        label: 'Service Logs',
        description: 'Monitor technical system logs and backend service performance.',
        icon: Icons.default.Terminal,
        path: '/admin/debug-logs'
    },
    discussions: {
        label: 'Discussions',
        description: 'Moderate community forums and student-teacher interactions.',
        icon: Icons.default.QuestionMark,
        path: '/admin/discussions'
    },
    exam_audit: {
        label: 'Exam Audit',
        description: 'Review examination materials, proctoring logs, and result integrity.',
        icon: Icons.default.QuizIcon,
        path: '/admin/exams'
    },
    feedback: {
        label: 'Feedback',
        description: 'Collect and analyze survey responses from students and faculty.',
        icon: Icons.default.Form,
        path: '/admin/feedback'
    },
    live_class_audit: {
        label: 'Live Class Audit',
        description: 'Monitor real-time virtual classrooms and meeting engagements.',
        icon: Icons.default.OndemandVideoIcon,
        path: '/admin/class-audit'
    },
    online_course: {
        label: 'Online Course',
        description: 'Manage digital course content, syllabi, and student enrollments.',
        icon: Icons.default.School,
        path: '/admin/online-classes'
    },
    recordings: {
        label: 'Recordings',
        description: 'Access and archive recorded sessions from previous live classes.',
        icon: Icons.default.OndemandVideoIcon,
        path: '/admin/recordings'
    },
    resume_builder: {
        label: 'Resume Builder',
        description: 'Manage professional profile templates and student resume data.',
        icon: Icons.default.Protrait,
        path: '/admin/resume-forms'
    },
    study_material_audit: {
        label: 'Study Materials',
        description: 'Review and organize educational resources and documents.',
        icon: Icons.default.DescriptionIcon,
        path: '/admin/study-materials'
    },
}

const SubadminDashboard = () => {

    const UserStore = useUserStore()

    const { layout, palette, border } = useTheme()

    const navigate = useNavigate()

    const activePermissions = Object.keys( UserStore.getUser.user_permissions ).filter( key => UserStore.getUser.user_permissions[key] === true )

    return (
        <Box padding={layout.pagePadding} overflow="auto" flexGrow={1}>
            <Box display="flex" flexDirection="column" gap={layout.sectionsGap} >
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems={{ sm: "center", xs: "stretch" }} gap="20px" flexWrap="wrap" justifyContent="space-between" >
                    <Box flexGrow={1}>
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">
                            Welcome back, You are logged in as a {UserStore.getUser.user_name}.
                        </Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary'>A comprehensive breakdown of system activity and resource distribution.</Typography>
                    </Box>
                </Box>

                <Box border={border[1]} bgcolor={palette.contentBg} padding="10px" borderRadius="10px" sx={{ display: 'flex', alignItems: 'center', gap: 2, color: palette.customThemeColor.main }}>
                    {Icons.default.Info}
                    <Box>
                        <Typography variant="subtitle2" >
                            Controlled Access Account
                        </Typography>
                        <Typography variant="body2" color='textSecondary'>
                            Your account is configured with specific permissions. For extra features, please contact
                            <Box component="a" className='default-link' href='mailto:alive.support@acharya.ac.in' sx={{ color: palette.customThemeColor.main, ml: 0.5, fontWeight: 500 }}>
                                Alive Support
                            </Box>.
                        </Typography>
                    </Box>
                </Box>

                <Box >
                    <Typography display="block" variant="subtitle2" sx={{ marginBottom: "10px", color: 'text.secondary', fontWeight: 500 }}>
                        Quick Access Modules
                    </Typography>

                    <Box
                        display="grid"
                        gridTemplateColumns={{
                            lg: "repeat(6,1fr)",
                            md: "repeat(4,1fr)",
                            sm: "repeat(2,1fr)",
                            xs: "repeat(1,1fr)",
                        }}
                        gap={layout.cardsGap}
                    >
                        {activePermissions.map( ( key ) => {
                            const meta = permissionMeta[key] || { label: key, icon: Icons.default.Settings, path: '#' }

                            return (
                                <Box key={key}>
                                    <Card sx={{
                                        bgcolor: palette.contentBg,
                                        border: border[1],
                                        borderRadius: 2,
                                        transition: '0.3s',
                                        '&:hover': { borderColor: palette.customThemeColor.main, transform: 'translateY(-4px)' }
                                    }}>
                                        <CardActionArea onClick={() => navigate( meta.path )}>
                                            <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                                <Box sx={{ color: palette.customThemeColor.main, fontSize: 40, mb: 1 }}>
                                                    {meta.icon}
                                                </Box>
                                                <Typography variant="subtitle1" fontWeight="600">
                                                    {meta.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {meta.description}
                                                </Typography>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Box>
                            )
                        } )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default SubadminDashboard
